<template>
    <div v-if="isDataLoaded" class="relative mx-auto my-0 flex size-full max-w-screen-xl flex-col justify-between">
        <div>
            <Header></Header>
        </div>
        <div class="flex size-full flex-auto flex-col xl:grid xl:grid-cols-4">
            <div class="size-full overflow-hidden rounded-none bg-content-background shadow-content-box-shadow xl:col-span-3 xl:rounded-t-2xl">
                <router-view></router-view>
            </div>
            <div v-if="!isMobile" class="col-span-1">
                <Sidebar></Sidebar>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { useJwt } from '@vueuse/integrations/useJwt';
import { usePrimeVue } from 'primevue/config';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import currencies from '@/codelists/Currencies';
import phoneMasksCodelist from '@/codelists/PhonePrefixes';
import { RequestTypeEnum } from '@/enums/RequestTypeEnum';
import { ROUTES } from '@/enums/RoutesEnum';
import Header from '@/layout/components/Header.vue';
import Sidebar from '@/layout/components/Sidebar.vue';
import router from '@/router';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useLoadingStore } from '@/stores/loading';
import { useUserStore } from '@/stores/user';
import { useWizardStore } from '@/stores/wizard';
import { getImage } from '@/utils/getImage';
import { parseBase32 } from '@/utils/parseBase32';

const authStore = useAuthStore();
const wizardStore = useWizardStore();
const userStore = useUserStore();
const loadingStore = useLoadingStore();

const breakpoints = useBreakpoints(breakpointsTailwind);
const toast = useToast();
const { t } = useI18n();
const primevue = usePrimeVue();

const isMobile = breakpoints.smaller('xl');
const defaultLanguage = 'cs';
const defaultCurrency = 'CZK';
const defaultPhonePrefix = '+420';

const isDataLoaded = ref(false);

// Init website data
onMounted(async () => {
    loadingStore.setIsLoading(true);
    isDataLoaded.value = false;
    try {
        if (authStore.getIsLoggedIn) {
            await initLoggedInUser();
        }
        await initWebsiteData();
        await parseAndProcessInitQueryParams();
    } catch (error) {
        console.log(error);
    } finally {
        loadingStore.setIsLoading(false);
        isDataLoaded.value = true;
    }
});

const initWebsiteData = async () => {
    try {
        const { data: websiteData } = await api.apiOnlineBookingMetadataByMiniWebsiteGet();

        // Set codelists
        if (websiteData.codelists) {
            userStore.setCodelists(websiteData.codelists);
        }

        // Set language
        const languageCode = websiteData.config?.ietfLanguageTag?.split('-')[0] || defaultLanguage;

        // Use language from BE otherwise local storage
        await userStore.setLanguage(!userStore.getLanguageCode ? languageCode : userStore.getLanguageCode, primevue);

        // Set currency
        const currencyCode = currencies.find((c) => c.symbol === websiteData.config?.currency)?.code || defaultCurrency; // TODO - fix BE - symbol is not currency code
        userStore.setCurrency(currencyCode);

        // Set phone prefix
        const phonePrefixCountyCode = phoneMasksCodelist.find((c) => c.prefix === websiteData.config?.phonePrefix)?.prefix || defaultPhonePrefix;
        userStore.setPhonePrefix(phonePrefixCountyCode);

        // Set clinics
        if (websiteData?.clinics?.length > 0) {
            userStore.setClinics(websiteData?.clinics);

            const allClinicsHaveGtm = websiteData?.clinics.every((clinic) => !!clinic.gtMid);

            if (allClinicsHaveGtm) {
                userStore.setGtmId(websiteData?.clinics[0].gtMid);
            }
        }
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: t('server-error'),
            life: 3000,
        });
        console.error(error);
    }
};

const preselectWizardData = async ({
    idLocation,
    openIdLocation,
    idService,
    openIdService,
    idStaff,
    openStaffId,
    defaultFormValues,
}: {
    idLocation: string | null;
    openIdLocation: string | null;
    idService: string | null;
    openIdService: string | null;
    idStaff: string | null;
    openStaffId: string | null;
    defaultFormValues: {
        firstName: string | null;
        lastName: string | null;
        email: string | null;
        phone: string | null;
    };
}) => {
    // If we have some default form values set them
    if (defaultFormValues) {
        userStore.setDefaultFormValues({
            firstName: defaultFormValues.firstName || '',
            lastName: defaultFormValues.lastName || '',
            email: defaultFormValues.email || '',
            phone: defaultFormValues.phone || '',
        });
    }

    if (openStaffId) {
        userStore.setOpenStaffId(openStaffId);
    }

    // If we have staff preselect staff without pushing to next step
    if (idStaff && (openIdLocation || idLocation) && (openIdService || idService)) {
        const clinic = userStore.getClinics.find((clinic) => clinic.id === idLocation || clinic.id === openIdLocation);
        const service = clinic?.services.find((service) => service.idService === idService || service.idService === openIdService);

        // Skip if service requires auth and user is not logged in
        if (service.requiresAuth && !authStore.getIsLoggedIn) {
            return;
        }

        if (clinic && service) {
            wizardStore.selectClinic(clinic);
            wizardStore.selectService(service);

            wizardStore.steps[0].isValid = true;
            wizardStore.steps[1].isValid = true;

            const staff = service.staffs.find((staff) => staff.idStaff === idStaff);

            if (staff) {
                userStore.setOpenStaffId(idStaff);
            }

            await router.push({
                name: ROUTES.STAFF_SELECTION,
            });
        }
    }

    // If we have selected service push directly to staff selection
    if (openIdService && (openIdLocation || idLocation)) {
        const clinic = userStore.getClinics.find((c) => c.id === openIdLocation || c.id === idLocation);
        const service = clinic?.services.find((s) => s.idService === openIdService);

        // Skip if service requires auth and user is not logged in
        if (service.requiresAuth && !authStore.getIsLoggedIn) {
            return;
        }

        if (clinic && service) {
            wizardStore.selectClinic(clinic);
            wizardStore.selectService(service);

            wizardStore.steps[0].isValid = true;
            wizardStore.steps[1].isValid = true;

            await router.push({
                name: ROUTES.STAFF_SELECTION,
            });
        }
        return;
    }

    // If we have selected clinic push directly to service selection
    if (openIdLocation) {
        const clinic = userStore.getClinics.find((c) => c.id === openIdLocation);
        if (clinic && clinic.services.length > 0) {
            wizardStore.selectClinic(clinic);

            wizardStore.steps[0].isValid = true;

            await router.push({
                name: ROUTES.SERVICE_SELECTION,
            });
        }
        return;
    }

    // If we have service preselect clinic and service without pushing to next step
    if (idService && (openIdLocation || idLocation)) {
        const clinic = userStore.getClinics.find((c) => c.id === openIdLocation || c.id === idLocation);
        const service = clinic?.services.find((s) => s.idService === idService);

        // Skip if service requires auth and user is not logged in
        if (service.requiresAuth && !authStore.getIsLoggedIn) {
            return;
        }

        if (clinic && service) {
            wizardStore.selectClinic({ ...clinic });
            wizardStore.selectService(service);
        }
        return;
    }

    // If we have clinic preselect clinic without pushing to next step
    if (idLocation) {
        const clinic = userStore.getClinics.find((c) => c.id === idLocation);
        if (clinic && clinic.services.length > 0) {
            wizardStore.selectClinic({ ...clinic });
        }
    }
};

const parseAndProcessInitQueryParams = async () => {
    const search = window.location.search + window.location.hash.replace('#', '');
    const urlParams = new URLSearchParams(search);

    const firstName = urlParams.get('firstName');
    const lastName = urlParams.get('lastName');
    const email = urlParams.get('email');
    const phone = urlParams.get('phone');
    const idLocation = urlParams.get('idLocation');
    const selectIdLocation = urlParams.get('selectIdLocation');
    const idService = urlParams.get('idService');
    const selectIdService = urlParams.get('selectIdService');
    const idStaff = urlParams.get('idStaff');
    const idSchema = urlParams.get('idScheme');
    const lang = urlParams.get('lang');
    const openStaffId = urlParams.get('openStaff');
    const token = urlParams.get('token');
    const idAppointmentRequest = urlParams.get('idAppointmentRequest');

    let colorHex = urlParams.get('color');

    if (colorHex) {
        if (colorHex.includes('#') === false) colorHex = '#' + colorHex;
    }

    if (idSchema) {
        userStore.setThemeFromUrl();
    }

    if (lang) {
        await userStore.setLanguage(lang);
    }

    if (colorHex && idSchema) {
        userStore.setTheme(colorHex, idSchema);
    }

    if (token && idAppointmentRequest) {
        const parsedToken = parseBase32(token);
        const { payload } = useJwt(parsedToken);

        // Logout user to clear previous data
        authStore.logout();

        authStore.setAccessToken({
            token: parsedToken,
            expiresIn: payload.value.exp * 1000, // Convert to ms
        });

        await initLoggedInUser(idAppointmentRequest);
    }

    // Clear query params // Commented out to keep query params for online marketing
    // await router.replace({ query: {} });

    // If we have some query params preselect wizard data
    let defaultFormValues = null;

    if (firstName || lastName || email || phone) {
        defaultFormValues = {
            firstName,
            lastName,
            email,
            phone,
        };
    }

    await preselectWizardData({
        idLocation,
        openIdLocation: selectIdLocation,
        idService,
        openIdService: selectIdService,
        idStaff,
        openStaffId,
        defaultFormValues,
    });
};

const initLoggedInUser = async (idAppointmentRequestId?: string) => {
    try {
        const { data } = await api.apiOnlineBookingGetOnlineBookingIklientInfoGet({
            metadata: {
                requestType: RequestTypeEnum.AUTHENTICATED,
            },
        });

        const profilePhoto = await getImage(data.user?.profilePhotoId);

        userStore.setLoggedInUser({
            userName: data.user?.userName || '',
            avatar: profilePhoto ? profilePhoto : '',
            appointments: data.appointmentRequests?.length || 0,
            idClient: data.userExtraInfo?.idClient || '',
            idCompany: data.userExtraInfo?.idCompany || '',
            idPartnership: data.userExtraInfo?.idPartnership || '',
            idLocation: data.userExtraInfo?.idLocation || '',
        });

        userStore.setDefaultFormValues({
            firstName: data.clientExtraInfo?.firstName || '',
            lastName: data.clientExtraInfo?.lastName || '',
            email: data.clientExtraInfo?.email || '',
            phonePrefix: data.clientExtraInfo?.phonePrefix || '',
            phone: data.clientExtraInfo?.phone || '',
            dateOfBirth: data.clientExtraInfo?.dateOfBirth || null,
        });

        if (idAppointmentRequestId) {
            const appointmentRequest = data.appointmentRequests.find((request) => request.idAppointmentRequest === idAppointmentRequestId);

            if (appointmentRequest) {
                wizardStore.canGoBack = false;
                wizardStore.setIdBudgetPlanService(appointmentRequest.idBudgetPlanService);
            }
        }
    } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 500) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: t('server-error'),
                life: 3000,
            });
        }
        console.error(error);
    }
};
</script>
